// src/pages/tuck_shop/RetailInventory.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, LinearProgress, Alert, Snackbar,
  TablePagination, InputAdornment, CircularProgress, Fab, Fade, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Card, CardContent, useTheme, alpha, Tab, Tabs,
  Avatar, Badge, Skeleton,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon, Search as SearchIcon,
  Clear as ClearIcon, Close as CloseIcon, Warning as WarningIcon,
  Info as InfoIcon, CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon, Inventory as InventoryIcon,
  History as HistoryIcon, ArrowUpward as InIcon,
  ArrowDownward as OutIcon, TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon, Storefront as StoreIcon,
  NotificationsActive as AlertIcon,
} from '@mui/icons-material';

import { ACCE_BRAND } from '../../theme/colors';
import Sidebar from '../../components/tuck_shop/Sidebar';
import Header from '../../components/tuck_shop/Header';
import Footer from '../../components/tuck_shop/Footer';
import '../../styles/Waiters.css';

// ════════════════════════════════════════════
// AXIOS CLIENT
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

// ── API helpers ──
const apiListProducts = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/products', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetProductStats = async () => {
  try {
    const { data } = await api.get('/tuckshop/products/stats');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetInventoryStats = async () => {
  try {
    const { data } = await api.get('/tuckshop/inventory/stats');
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetProductStock = async (productId) => {
  try {
    const { data } = await api.get(`/tuckshop/products/${productId}/stock`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiAddProductStock = async (productId, payload) => {
  try {
    const { data } = await api.post(`/tuckshop/products/${productId}/stock`, payload);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════
const TUCKSHOP = {
  name: 'Tuckshop',
  color: ACCE_BRAND.orange,
  lightColor: ACCE_BRAND.orangeLight,
  icon: '🏪',
};

const STOCK_STATUS = {
  IN_STOCK:     { label: 'In Stock',     color: '#4caf50' },
  LOW_STOCK:    { label: 'Low Stock',    color: '#ff9800' },
  OUT_OF_STOCK: { label: 'Out of Stock', color: '#f44336' },
};

const MOVEMENT_LABELS = {
  PURCHASE: 'Purchase', RESTOCK: 'Restock', SALE: 'Sale', RETURN: 'Return',
  ADJUSTMENT: 'Adjustment', WASTAGE: 'Wastage', TRANSFER: 'Transfer',
};
const MOVEMENT_COLORS = {
  PURCHASE: '#4caf50', RESTOCK: '#2196f3', SALE: '#f44336', RETURN: '#00bcd4',
  ADJUSTMENT: '#ff9800', WASTAGE: '#9e9e9e', TRANSFER: '#9c27b0',
};

const SELECT_MIN_WIDTH = 160;

// ── Safe numeric helpers ──
const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const toIntOrZero = (v) => Math.trunc(toNumber(v, 0));

// Format currency as ₦ with 2 decimals and thousands separators
const formatNaira = (v) => {
  const n = toNumber(v, 0);
  return `₦${n.toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Format quantity as an integer with thousands separators
const formatQty = (v) => {
  const n = toIntOrZero(v);
  return n.toLocaleString('en-NG');
};

function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════

const StatCard = React.memo(({ title, value, icon, color, loading }) => (
  <Card
    sx={{
      borderRadius: 2,
      borderLeft: `4px solid ${color}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      transition: 'transform .2s, box-shadow .2s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
    }}
  >
    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.5}>
          {title}
        </Typography>
        <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 32, height: 32 }}>
          {icon}
        </Avatar>
      </Box>
      {loading ? (
        <Skeleton variant="text" width="60%" height={36} />
      ) : (
        <Typography variant="h5" sx={{ fontWeight: 800, color, mt: 0.5 }}>
          {value}
        </Typography>
      )}
    </CardContent>
  </Card>
));
StatCard.displayName = 'StatCard';

const ProductRow = React.memo(({
  product, index, onRestock, onAdjust, onViewHistory, onViewDetails,
}) => {
  const theme = useTheme();
  // Safer fallback: no status = out of stock, not in stock
  const statusKey = product.status || 'OUT_OF_STOCK';
  const cfg = STOCK_STATUS[statusKey] || STOCK_STATUS.OUT_OF_STOCK;
  const isLowOrOut = statusKey === 'LOW_STOCK' || statusKey === 'OUT_OF_STOCK';
  const qty = toIntOrZero(product.quantity);
  const price = toNumber(product.price, 0);
  const mainCatColor = product.main_category === 'Food' ? '#FF6F00' : '#00695C';

  return (
    <TableRow hover sx={isLowOrOut ? { bgcolor: alpha(cfg.color, 0.03) } : undefined}>
      <TableCell>{index + 1}</TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>{product.name}</Typography>
            {product.category && (
              <Typography variant="caption" color="text.secondary">{product.category}</Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={product.main_category || '—'}
          size="small"
          sx={{
            fontWeight: 700,
            bgcolor: alpha(mainCatColor, 0.1),
            color: mainCatColor,
            fontSize: '0.72rem',
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight={700}>
          {formatNaira(price)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Typography variant="body2" fontWeight={800} sx={{ color: cfg.color }}>
          {formatQty(qty)}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={cfg.label}
          size="small"
          sx={{ bgcolor: alpha(cfg.color, 0.1), color: cfg.color, fontWeight: 700, fontSize: '0.7rem' }}
        />
      </TableCell>
      <TableCell align="center">
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
          <Tooltip title="Restock">
            <IconButton size="small" onClick={() => onRestock(product)} sx={{ color: '#4caf50' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Adjust">
            <IconButton size="small" onClick={() => onAdjust(product)} sx={{ color: theme.palette.warning.main }}>
              <TrendingUpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="History">
            <IconButton size="small" onClick={() => onViewHistory(product)} sx={{ color: theme.palette.info.main }}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Details">
            <IconButton size="small" onClick={() => onViewDetails(product)} sx={{ color: theme.palette.primary.main }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
});
ProductRow.displayName = 'ProductRow';

const MovementRow = React.memo(({ m, showProduct = false }) => {
  const isIn = ['PURCHASE', 'RESTOCK', 'RETURN'].includes(m.movement_type);
  const color = MOVEMENT_COLORS[m.movement_type] || '#9e9e9e';
  const qty = Math.abs(toIntOrZero(m.quantity));
  return (
    <TableRow>
      <TableCell>{new Date(m.created_at).toLocaleString()}</TableCell>
      <TableCell>
        <Chip
          label={MOVEMENT_LABELS[m.movement_type] || m.movement_type}
          size="small"
          sx={{ bgcolor: alpha(color, 0.1), color, fontWeight: 600 }}
          icon={isIn ? <InIcon sx={{ fontSize: 14 }} /> : <OutIcon sx={{ fontSize: 14 }} />}
        />
      </TableCell>
      {showProduct && <TableCell>{m.product_name || '-'}</TableCell>}
      <TableCell sx={{ color: isIn ? '#4caf50' : '#f44336', fontWeight: 700 }}>
        {isIn ? '+' : '−'}{formatQty(qty)} {m.unit || ''}
      </TableCell>
      <TableCell>
        {formatQty(m.previous_quantity)} → {formatQty(m.new_quantity)}
      </TableCell>
      <TableCell>{m.reason || '-'}</TableCell>
      <TableCell>{m.created_by || '-'}</TableCell>
    </TableRow>
  );
});
MovementRow.displayName = 'MovementRow';

const AlertRow = React.memo(({ item, color, onRestock }) => {
  const qty = toIntOrZero(item.quantity);
  const reorder = toIntOrZero(item.reorder_point);
  return (
    <Box
      sx={{
        px: 2.5, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 2,
        borderBottom: '1px solid', borderColor: 'divider',
        '&:hover': { bgcolor: alpha(color, 0.04) },
      }}
    >
      <Avatar sx={{ bgcolor: alpha(color, 0.15), color, width: 36, height: 36 }}>
        {item.status === 'OUT_OF_STOCK' ? <CancelIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap>{item.name}</Typography>
        <Typography variant="caption" color="text.secondary">
          {item.category ? `${item.category} • ` : ''}
          Qty: <strong style={{ color }}>{formatQty(qty)}</strong>
          {item.reorder_point != null && ` / Reorder: ${formatQty(reorder)}`}
        </Typography>
      </Box>
      <Button
        size="small"
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => onRestock(item)}
        sx={{ bgcolor: color, '&:hover': { bgcolor: alpha(color, 0.85) }, textTransform: 'none', fontWeight: 700 }}
      >
        Restock
      </Button>
    </Box>
  );
});
AlertRow.displayName = 'AlertRow';

const StockAlertDialog = React.memo(({ open, onClose, outOfStock, lowStock, onRestock }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Fade}>
    <DialogTitle
      sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        bgcolor: outOfStock.length ? alpha('#f44336', 0.08) : alpha('#ff9800', 0.08),
        borderBottom: '1px solid', borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <AlertIcon sx={{ color: outOfStock.length ? '#f44336' : '#ff9800', fontSize: 32 }} />
        <Box>
          <Typography variant="h6" fontWeight={800}>Stock Alert</Typography>
          <Typography variant="caption" color="text.secondary">
            {outOfStock.length > 0 && `${outOfStock.length} out of stock`}
            {outOfStock.length > 0 && lowStock.length > 0 && ' • '}
            {lowStock.length > 0 && `${lowStock.length} low on stock`}
          </Typography>
        </Box>
      </Box>
      <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 0, maxHeight: '60vh' }}>
      {outOfStock.length > 0 && (
        <Box>
          <Box sx={{ px: 2.5, py: 1, bgcolor: alpha('#f44336', 0.1), borderBottom: `2px solid ${alpha('#f44336', 0.2)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#f44336' }}>
              OUT OF STOCK ({outOfStock.length})
            </Typography>
          </Box>
          {outOfStock.map((p) => <AlertRow key={p.id} item={p} color="#f44336" onRestock={onRestock} />)}
        </Box>
      )}
      {lowStock.length > 0 && (
        <Box sx={{ mt: outOfStock.length ? 2 : 0 }}>
          <Box sx={{ px: 2.5, py: 1, bgcolor: alpha('#ff9800', 0.1), borderBottom: `2px solid ${alpha('#ff9800', 0.2)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
            <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#ff9800' }}>
              LOW ON STOCK ({lowStock.length})
            </Typography>
          </Box>
          {lowStock.map((p) => <AlertRow key={p.id} item={p} color="#ff9800" onRestock={onRestock} />)}
        </Box>
      )}
      {outOfStock.length === 0 && lowStock.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: '#4caf50' }} />
          <Typography variant="h6" sx={{ mt: 2 }}>All Good!</Typography>
          <Typography variant="body2" color="text.secondary">
            Every product is above its reorder level.
          </Typography>
        </Box>
      )}
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} color="inherit">Dismiss</Button>
    </DialogActions>
  </Dialog>
));
StockAlertDialog.displayName = 'StockAlertDialog';

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const RetailInventory = () => {
  const theme = useTheme();
  const locationColor = TUCKSHOP.color;
  const locationName = TUCKSHOP.name;
  const [activeMenu, setActiveMenu] = useState('Inventory');

  // ---------- Products ----------
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [productsLoading, setProductsLoading] = useState(true);

  // ---------- Stats ----------
  const [invStats, setInvStats] = useState(null);
  const [prodStats, setProdStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // ---------- Movements ----------
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsLoaded, setMovementsLoaded] = useState(false);

  // ---------- Table controls ----------
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');
  const debouncedSearch = useDebounce(searchTerm, 350);

  // ---------- Alert popup ----------
  const [alertPopupOpen, setAlertPopupOpen] = useState(false);
  const popupShown = useRef(false);

  // ---------- Restock ----------
  const [restockOpen, setRestockOpen] = useState(false);
  const [restockProduct, setRestockProduct] = useState(null);
  const [restockForm, setRestockForm] = useState({
    quantity: '', cost_per_unit: '', supplier: '', notes: '',
  });

  // ---------- Adjust ----------
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustProduct, setAdjustProduct] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ target: '', reason: '', notes: '' });

  // ---------- History ----------
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ---------- Details ----------
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsProduct, setDetailsProduct] = useState(null);
  const [detailsInventory, setDetailsInventory] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // ---------- Submission / Snackbar ----------
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const showSnackbar = useCallback((message, severity = 'success') => {
    if (isMounted.current) setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  // ════════════════════════════════════════════
  // FETCH: PRODUCTS
  // Sends status_filter to the server (now supported by the backend)
  // ════════════════════════════════════════════
  const fetchProducts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setProductsLoading(true);
    const params = {
      page: page + 1,
      page_size: rowsPerPage,
      sort_by: orderBy,
      sort_dir: order,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (statusFilter !== 'all') params.status_filter = statusFilter;

    const res = await apiListProducts(params);
    if (!isMounted.current) return;

    if (res.success) {
      setProducts(res.data || []);
      setTotalProducts(res.total || 0);
    } else {
      showSnackbar(res.error || 'Failed to load products', 'error');
      setProducts([]);
      setTotalProducts(0);
    }
    if (!silent) setProductsLoading(false);
  }, [page, rowsPerPage, orderBy, order, debouncedSearch, statusFilter, showSnackbar]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // ════════════════════════════════════════════
  // FETCH: STATS
  // ════════════════════════════════════════════
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    const [invRes, prodRes] = await Promise.all([
      apiGetInventoryStats(),
      apiGetProductStats(),
    ]);
    if (!isMounted.current) return;
    if (invRes.success) setInvStats(invRes.stats);
    if (prodRes.success) setProdStats(prodRes.stats);
    setStatsLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ════════════════════════════════════════════
  // FETCH: GLOBAL MOVEMENTS (lazy — when Tab 2 opens)
  // ════════════════════════════════════════════
  const fetchAllMovements = useCallback(async () => {
    setMovementsLoading(true);
    try {
      const rows = await Promise.all(
        products.slice(0, 25).map(async (p) => {
          const res = await apiGetProductStock(p.id);
          if (!res.success) return [];
          return (res.movements || []).map((m) => ({ ...m, product_name: p.name }));
        })
      );
      const flat = rows.flat().sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      if (isMounted.current) setMovements(flat);
    } finally {
      if (isMounted.current) {
        setMovementsLoading(false);
        setMovementsLoaded(true);
      }
    }
  }, [products]);

  useEffect(() => {
    if (tab === 2 && !movementsLoaded) fetchAllMovements();
  }, [tab, movementsLoaded, fetchAllMovements]);

  // ════════════════════════════════════════════
  // FETCH: PRODUCT STOCK
  // ════════════════════════════════════════════
  const fetchProductStock = useCallback(async (productId) => {
    return await apiGetProductStock(productId);
  }, []);

  // ════════════════════════════════════════════
  // ALERTS — derived from products (safe fallback)
  // ════════════════════════════════════════════
  const outOfStockAlerts = useMemo(
    () => products.filter((p) => (p.status || 'OUT_OF_STOCK') === 'OUT_OF_STOCK'),
    [products]
  );
  const lowStockAlerts = useMemo(
    () => products.filter((p) => p.status === 'LOW_STOCK'),
    [products]
  );
  const totalAlerts = outOfStockAlerts.length + lowStockAlerts.length;

  useEffect(() => {
    if (popupShown.current) return;
    if (!statsLoading && totalAlerts > 0) {
      popupShown.current = true;
      setAlertPopupOpen(true);
    }
  }, [statsLoading, totalAlerts]);

  // ════════════════════════════════════════════
  // SORT
  // ════════════════════════════════════════════
  const handleSort = useCallback((prop) => {
    setOrder((prev) => (orderBy === prop && prev === 'asc' ? 'desc' : 'asc'));
    setOrderBy(prop);
    setPage(0);
  }, [orderBy]);

  // ════════════════════════════════════════════
  // REFRESH
  // ════════════════════════════════════════════
  const handleRefresh = useCallback(() => {
    showSnackbar('Refreshing…', 'info');
    fetchProducts({ silent: true });
    fetchStats();
    setMovementsLoaded(false);
  }, [fetchProducts, fetchStats, showSnackbar]);

  // ════════════════════════════════════════════
  // RESTOCK
  // ════════════════════════════════════════════
  const handleOpenRestock = useCallback((p) => {
    setRestockProduct(p);
    setRestockForm({
      quantity: '',
      cost_per_unit: p.cost_per_unit ? String(p.cost_per_unit) : '',
      supplier: '',
      notes: '',
    });
    setRestockOpen(true);
  }, []);

  const handleRestockSubmit = useCallback(async () => {
    if (!restockProduct) return;
    const qty = Number(restockForm.quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      showSnackbar('Enter a valid quantity greater than 0', 'error');
      return;
    }
    setSubmitting(true);
    const res = await apiAddProductStock(restockProduct.id, {
      quantity: qty,
      cost_per_unit: restockForm.cost_per_unit !== '' ? Number(restockForm.cost_per_unit) : null,
      supplier: restockForm.supplier?.trim() || null,
      notes: restockForm.notes?.trim() || null,
      unit: 'unit',
    });
    setSubmitting(false);
    if (!res.success) {
      showSnackbar(res.error, 'error');
      return;
    }
    showSnackbar(`✅ Added ${qty} to "${restockProduct.name}"`, 'success');
    setRestockOpen(false);
    fetchProducts({ silent: true });
    fetchStats();
    setMovementsLoaded(false);
  }, [restockProduct, restockForm, showSnackbar, fetchProducts, fetchStats]);

  // ════════════════════════════════════════════
  // ADJUST
  // ════════════════════════════════════════════
  const handleOpenAdjust = useCallback((p) => {
    setAdjustProduct(p);
    setAdjustForm({ target: String(toIntOrZero(p.quantity)), reason: '', notes: '' });
    setAdjustOpen(true);
  }, []);

  const handleAdjustSubmit = useCallback(async () => {
    if (!adjustProduct) return;
    const rawTarget = adjustForm.target;
    if (rawTarget === '' || !Number.isFinite(Number(rawTarget)) || Number(rawTarget) < 0) {
      showSnackbar('Enter a valid non-negative target quantity', 'error');
      return;
    }
    if (!adjustForm.reason.trim()) {
      showSnackbar('A reason is required for adjustments', 'error');
      return;
    }
    const current = toIntOrZero(adjustProduct.quantity);
    const target = Math.trunc(Number(rawTarget));
    const delta = target - current;

    if (delta === 0) {
      showSnackbar('No change to apply — target equals current quantity.', 'info');
      return;
    }

    setSubmitting(true);
    if (delta > 0) {
      const res = await apiAddProductStock(adjustProduct.id, {
        quantity: delta,
        notes: adjustForm.notes || null,
        supplier: null,
        cost_per_unit: null,
        unit: 'unit',
      });
      setSubmitting(false);
      if (!res.success) { showSnackbar(res.error, 'error'); return; }
      showSnackbar(`✅ Adjusted "${adjustProduct.name}": ${current} → ${target}`, 'success');
    } else {
      setSubmitting(false);
      showSnackbar('⚠️ Negative adjustments require a dedicated endpoint — not yet implemented.', 'warning');
      return;
    }
    setAdjustOpen(false);
    fetchProducts({ silent: true });
    fetchStats();
    setMovementsLoaded(false);
  }, [adjustProduct, adjustForm, showSnackbar, fetchProducts, fetchStats]);

  const adjustPreview = useMemo(() => {
    if (!adjustProduct) return { current: 0, target: null, delta: null, valid: false };
    const current = toIntOrZero(adjustProduct.quantity);
    const raw = adjustForm.target;
    if (raw === '' || raw === null || raw === undefined) return { current, target: null, delta: null, valid: false };
    const targetNum = Number(raw);
    if (!Number.isFinite(targetNum) || targetNum < 0) return { current, target: null, delta: null, valid: false };
    const target = Math.trunc(targetNum);
    return { current, target, delta: target - current, valid: true };
  }, [adjustProduct, adjustForm.target]);

  // ════════════════════════════════════════════
  // HISTORY
  // ════════════════════════════════════════════
  const handleOpenHistory = useCallback(async (p) => {
    setHistoryProduct(p);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryItems([]);
    const res = await fetchProductStock(p.id);
    if (!isMounted.current) return;
    if (res.success) {
      setHistoryItems(res.movements || []);
    } else {
      showSnackbar(res.error || 'Failed to load history', 'error');
    }
    setHistoryLoading(false);
  }, [fetchProductStock, showSnackbar]);

  // ════════════════════════════════════════════
  // DETAILS
  // ════════════════════════════════════════════
  const handleOpenDetails = useCallback(async (p) => {
    setDetailsProduct(p);
    setDetailsInventory(null);
    setDetailsOpen(true);
    setDetailsLoading(true);
    const res = await fetchProductStock(p.id);
    if (!isMounted.current) return;
    if (res.success) {
      setDetailsProduct(res.product || p);
      setDetailsInventory(res.inventory);
    }
    setDetailsLoading(false);
  }, [fetchProductStock]);

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════
  return (
    <>
      <div className="epos-wrapper">
        <div className="epos-main-layout">
          <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

          <div className="epos-main">
            <Header />

            <div className="epos-content">
              {/* HERO */}
              <div className="hero-card">
                <div>
                  <div className="hero-title" style={{ color: locationColor, fontWeight: 800 }}>
                    {TUCKSHOP.icon} {locationName} Inventory
                  </div>
                  <div className="hero-text-wrapper">
                    <div className="hero-tagline">
                      <span className="hero-tagline-icon">✦</span>
                      Real-time stock tracking for every product
                    </div>
                  </div>
                </div>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {totalAlerts > 0 && (
                    <Button
                      variant="contained"
                      startIcon={
                        <Badge badgeContent={totalAlerts} color="error">
                          <AlertIcon />
                        </Badge>
                      }
                      onClick={() => setAlertPopupOpen(true)}
                      sx={{
                        borderRadius: 2,
                        bgcolor: outOfStockAlerts.length ? '#f44336' : '#ff9800',
                        '&:hover': { bgcolor: outOfStockAlerts.length ? '#d32f2f' : '#f57c00' },
                        textTransform: 'none', fontWeight: 700,
                      }}
                    >
                      Stock Alerts
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Refresh
                  </Button>
                </Box>
              </div>

              {/* BANNER */}
              {totalAlerts > 0 && (
                <Alert
                  severity={outOfStockAlerts.length ? 'error' : 'warning'}
                  icon={outOfStockAlerts.length ? <CancelIcon /> : <WarningIcon />}
                  sx={{ mb: 3, borderRadius: 2, alignItems: 'center' }}
                  action={
                    <Button color="inherit" size="small" variant="outlined" onClick={() => setAlertPopupOpen(true)}>
                      View {totalAlerts}
                    </Button>
                  }
                >
                  <Typography variant="body2" fontWeight={700}>
                    {outOfStockAlerts.length > 0 && `${outOfStockAlerts.length} out of stock`}
                    {outOfStockAlerts.length > 0 && lowStockAlerts.length > 0 && ' • '}
                    {lowStockAlerts.length > 0 && `${lowStockAlerts.length} low on stock`}
                  </Typography>
                  <Typography variant="caption">
                    {[...outOfStockAlerts, ...lowStockAlerts].slice(0, 3).map((a) => a.name).join(', ')}
                    {totalAlerts > 3 && ` +${totalAlerts - 3} more`}
                  </Typography>
                </Alert>
              )}

              {/* STATS — all numeric, all safely formatted */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { title: 'Total Products',  value: prodStats?.total ?? 0, color: locationColor, icon: <InventoryIcon fontSize="small" /> },
                  { title: 'Active',          value: prodStats?.active ?? 0, color: '#4caf50', icon: <CheckCircleIcon fontSize="small" /> },
                  { title: 'Low Stock',       value: invStats?.low_stock ?? 0, color: '#ff9800', icon: <WarningIcon fontSize="small" /> },
                  { title: 'Out of Stock',    value: invStats?.out_of_stock ?? 0, color: '#f44336', icon: <CancelIcon fontSize="small" /> },
                  { title: 'With Inventory',  value: invStats?.with_inventory ?? 0, color: '#2196f3', icon: <StoreIcon fontSize="small" /> },
                  { title: 'Stock Value',     value: formatNaira(invStats?.total_stock_value ?? 0), color: '#9c27b0', icon: <MoneyIcon fontSize="small" /> },
                ].map((s) => (
                  <Grid item xs={12} sm={6} md={4} lg={2} key={s.title}>
                    <StatCard title={s.title} value={s.value} icon={s.icon} color={s.color} loading={statsLoading} />
                  </Grid>
                ))}
              </Grid>

              {/* TABS */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
                  <Tab label="📦 Products" />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>⚠️ Alerts</span>
                        {totalAlerts > 0 && (
                          <Chip
                            size="small" label={totalAlerts}
                            sx={{
                              height: 18, fontSize: '0.7rem', fontWeight: 700,
                              bgcolor: outOfStockAlerts.length ? '#f44336' : '#ff9800',
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                  <Tab label="📈 Movements" />
                </Tabs>
              </Box>

              {productsLoading && <LinearProgress sx={{ mb: 2 }} />}

              {/* TAB 0 — PRODUCTS */}
              {tab === 0 && (
                <Box>
                  <Paper sx={{ p: 2, mb: 3, borderRadius: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                      size="small"
                      placeholder="Search by name, category…"
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                      sx={{ flex: 1, minWidth: 220, maxWidth: 420 }}
                      InputProps={{
                        startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                        endAdornment: searchTerm && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: SELECT_MIN_WIDTH }}>
                      <InputLabel>Stock Status</InputLabel>
                      <Select value={statusFilter} label="Stock Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="in_stock">In Stock</MenuItem>
                        <MenuItem value="low_stock">Low Stock</MenuItem>
                        <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary">
                      {totalProducts.toLocaleString()} products
                    </Typography>
                  </Paper>

                  <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                            <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>
                              <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>
                                Product
                              </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Main Category</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              <TableSortLabel active={orderBy === 'price'} direction={orderBy === 'price' ? order : 'asc'} onClick={() => handleSort('price')}>
                                Price
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Qty</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productsLoading && products.length === 0 ? (
                            [...Array(6)].map((_, i) => (
                              <TableRow key={i}>
                                {[...Array(7)].map((__, j) => (
                                  <TableCell key={j}><Skeleton variant="text" /></TableCell>
                                ))}
                              </TableRow>
                            ))
                          ) : products.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="textSecondary">No products found</Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            products.map((p, i) => (
                              <ProductRow
                                key={p.id}
                                product={p}
                                index={page * rowsPerPage + i}
                                onRestock={handleOpenRestock}
                                onAdjust={handleOpenAdjust}
                                onViewHistory={handleOpenHistory}
                                onViewDetails={handleOpenDetails}
                              />
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={totalProducts}
                      page={page}
                      onPageChange={(_, np) => setPage(np)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                  </Paper>
                </Box>
              )}

              {/* TAB 1 — ALERTS */}
              {tab === 1 && (
                <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  {totalAlerts === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <CheckCircleIcon sx={{ fontSize: 56, color: '#4caf50' }} />
                      <Typography variant="h6" sx={{ mt: 2 }}>All Good!</Typography>
                      <Typography variant="body2" color="text.secondary">
                        No products are low or out of stock.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {outOfStockAlerts.length > 0 && (
                        <Box>
                          <Box sx={{ px: 2.5, py: 1, bgcolor: alpha('#f44336', 0.1), borderBottom: `2px solid ${alpha('#f44336', 0.2)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CancelIcon sx={{ color: '#f44336', fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#f44336' }}>
                              OUT OF STOCK ({outOfStockAlerts.length})
                            </Typography>
                          </Box>
                          {outOfStockAlerts.map((p) => <AlertRow key={p.id} item={p} color="#f44336" onRestock={handleOpenRestock} />)}
                        </Box>
                      )}
                      {lowStockAlerts.length > 0 && (
                        <Box sx={{ mt: outOfStockAlerts.length ? 2 : 0 }}>
                          <Box sx={{ px: 2.5, py: 1, bgcolor: alpha('#ff9800', 0.1), borderBottom: `2px solid ${alpha('#ff9800', 0.2)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#ff9800' }}>
                              LOW ON STOCK ({lowStockAlerts.length})
                            </Typography>
                          </Box>
                          {lowStockAlerts.map((p) => <AlertRow key={p.id} item={p} color="#ff9800" onRestock={handleOpenRestock} />)}
                        </Box>
                      )}
                    </>
                  )}
                </Paper>
              )}

              {/* TAB 2 — MOVEMENTS */}
              {tab === 2 && (
                <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                  {movementsLoading && <LinearProgress />}
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Change</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {movements.length === 0 && !movementsLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No movements recorded yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          movements.map((m) => <MovementRow key={m.id} m={m} showProduct />)
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>

      {/* ═══════ STOCK ALERT DIALOG ═══════ */}
      <StockAlertDialog
        open={alertPopupOpen}
        onClose={() => setAlertPopupOpen(false)}
        outOfStock={outOfStockAlerts}
        lowStock={lowStockAlerts}
        onRestock={handleOpenRestock}
      />

      {/* ═══════ RESTOCK DIALOG ═══════ */}
      <Dialog open={restockOpen} onClose={() => setRestockOpen(false)} maxWidth="sm" fullWidth TransitionComponent={Fade}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <AddIcon sx={{ mr: 1, color: '#4caf50', verticalAlign: 'middle' }} />
              Restock Product
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {restockProduct?.name} — Current: {formatQty(restockProduct?.quantity)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setRestockOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required type="number" label="Quantity to Add"
                value={restockForm.quantity}
                onChange={(e) => setRestockForm((f) => ({ ...f, quantity: e.target.value }))}
                inputProps={{ min: 1, step: 1 }} autoFocus />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth type="number" label="Unit Cost (₦)"
                value={restockForm.cost_per_unit}
                onChange={(e) => setRestockForm((f) => ({ ...f, cost_per_unit: e.target.value }))}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Supplier"
                value={restockForm.supplier}
                onChange={(e) => setRestockForm((f) => ({ ...f, supplier: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes"
                value={restockForm.notes}
                onChange={(e) => setRestockForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRestockOpen(false)} color="inherit" disabled={submitting}>Cancel</Button>
          <Button variant="contained" disabled={submitting || !restockForm.quantity}
            onClick={handleRestockSubmit}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}>
            {submitting ? 'Saving…' : 'Confirm Restock'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ ADJUST DIALOG ═══════ */}
      <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="sm" fullWidth TransitionComponent={Fade}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <TrendingUpIcon sx={{ mr: 1, color: theme.palette.warning.main, verticalAlign: 'middle' }} />
              Adjust Stock
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {adjustProduct?.name} — Current: {formatQty(adjustProduct?.quantity)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAdjustOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the <strong>new absolute quantity</strong>. The system will compute the delta.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth required type="number" label="New Quantity"
                value={adjustForm.target}
                onChange={(e) => setAdjustForm((f) => ({ ...f, target: e.target.value }))}
                inputProps={{ min: 0, step: 1 }} autoFocus
                error={adjustForm.target !== '' && (!Number.isFinite(Number(adjustForm.target)) || Number(adjustForm.target) < 0)}
                helperText={adjustForm.target !== '' && (!Number.isFinite(Number(adjustForm.target)) || Number(adjustForm.target) < 0) ? 'Enter a non-negative whole number' : ' '} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="Reason"
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Stock take correction, damaged units" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes"
                value={adjustForm.notes}
                onChange={(e) => setAdjustForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
            {adjustPreview.valid && adjustPreview.target !== null && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: alpha(theme.palette.warning.main, 0.06), border: `1px dashed ${alpha(theme.palette.warning.main, 0.35)}` }}>
                  <Typography variant="body2" color="text.secondary" component="div">
                    Change preview: <strong>{formatQty(adjustPreview.current)}</strong> → <strong>{formatQty(adjustPreview.target)}</strong>{' '}
                    <span style={{
                      color: adjustPreview.delta > 0 ? '#4caf50' : adjustPreview.delta < 0 ? '#f44336' : 'inherit',
                      fontWeight: 700,
                    }}>
                      ({adjustPreview.delta >= 0 ? '+' : ''}{formatQty(adjustPreview.delta)})
                    </span>
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setAdjustOpen(false)} color="inherit" disabled={submitting}>Cancel</Button>
          <Button variant="contained" color="warning"
            disabled={submitting || !adjustPreview.valid || adjustPreview.target === null || !adjustForm.reason.trim() || adjustPreview.delta === 0}
            onClick={handleAdjustSubmit}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}>
            {submitting ? 'Saving…' : 'Save Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ HISTORY DIALOG ═══════ */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="lg" fullWidth TransitionComponent={Fade}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Movement History
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {historyProduct?.name} {historyProduct?.category ? `• ${historyProduct.category}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHistoryOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : historyItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">No stock movements recorded for this product.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Previous</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>New</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((m) => <MovementRow key={m.id} m={m} />)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ DETAILS DIALOG ═══════ */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth TransitionComponent={Fade}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>Product Details</Typography>
          <IconButton size="small" onClick={() => setDetailsOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailsLoading ? (
            <Box>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={120} />
            </Box>
          ) : detailsProduct ? (
            <Grid container spacing={2}>
              {[
                ['Name', detailsProduct.name],
                ['Category', detailsProduct.category || '-'],
                ['Main Category', detailsProduct.main_category || '-'],
                ['Description', detailsProduct.description || '-'],
                ['Price', formatNaira(detailsProduct.price)],
                ['Current Qty', detailsInventory ? formatQty(detailsInventory.quantity) : 'N/A'],
                ['Unit', detailsInventory?.unit || 'unit'],
                ['Reorder Point', detailsInventory ? formatQty(detailsInventory.reorder_point) : 'N/A'],
                ['Cost Per Unit', detailsInventory?.cost_per_unit ? formatNaira(detailsInventory.cost_per_unit) : '-'],
                ['Stock Status', detailsInventory ? (STOCK_STATUS[detailsInventory.status]?.label || detailsInventory.status) : 'No inventory'],
                ['Active', detailsProduct.is_active ? 'Yes' : 'No'],
                ['Created Date', detailsProduct.created_date ? String(detailsProduct.created_date).split('T')[0] : '-'],
              ].map(([k, v]) => (
                <Grid item xs={6} key={k}>
                  <Typography variant="caption" color="text.secondary">{k}</Typography>
                  <Typography variant="body2" fontWeight={600}>{v}</Typography>
                </Grid>
              ))}
            </Grid>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════ SNACKBAR ═══════ */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} TransitionComponent={Fade}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ═══════ FAB ═══════ */}
      <Fab color="primary" sx={{
        position: 'fixed', bottom: 24, right: 24,
        display: { xs: 'flex', sm: 'none' }, zIndex: 1000,
        bgcolor: locationColor, '&:hover': { bgcolor: locationColor, opacity: 0.85 },
      }} onClick={() => products.length && handleOpenRestock(products[0])}>
        <AddIcon />
      </Fab>
    </>
  );
};

export default RetailInventory;